// # User Notifications Page Component
import React, { useEffect, useState } from 'react';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { BsCheckCircle, BsPlayCircle, BsArrowClockwise, BsStopCircle, BsPencil, BsAlarm, BsTrash } from 'react-icons/bs';
import { FaChalkboardTeacher, FaVideo } from 'react-icons/fa';
import sessionManager from '../../../auth/session';
import notificationManager from '../../../auth/sessions/notification_session';
import './notif_page.css';
import { Link } from 'react-router-dom';

// # Get icon based on notification action
const getNotificationIcon = (action) => {
  switch (action) {
    case 'activate':
      return <BsPlayCircle className="notif_page_item_icon activate" />;
    case 'extend':
      return <BsArrowClockwise className="notif_page_item_icon extend" />;
    case 'reduce':
      return <BsArrowClockwise className="notif_page_item_icon reduce" />;
    case 'deactivate':
      return <BsStopCircle className="notif_page_item_icon deactivate" />;
    case 'ended':
      return <BsStopCircle className="notif_page_item_icon ended" />;
    case 'assign_mentor':
      return <FaChalkboardTeacher className="notif_page_item_icon mentor" />;
    case 'edit':
      return <BsPencil className="notif_page_item_icon edit" />;
    case 'ending_soon':
      return <BsAlarm className="notif_page_item_icon ending" />;
    case 'meeting':
      return <FaVideo className="notif_page_item_icon meeting" />;
    default:
      return null;
  }
};

const UserNotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  
  useEffect(() => {
    // # Get notifications from session cache
    const session = sessionManager.getSession();
    if (session) {
      // # Initialize notifications first
      notificationManager.initializeNotifications(session.uid).then(() => {
        const allNotifications = [];
        const notificationState = notificationManager.getNotifications();
        
        // # Add regular subscription notifications
        if (notificationState.notifications) {
          Object.values(notificationState.notifications).forEach(notif => {
            allNotifications.push(notif);
          });
        }
        
        // # Add ending subscription notifications
        if (notificationState.subsc_ending) {
          Object.values(notificationState.subsc_ending).forEach(notif => {
            allNotifications.push(notif);
          });
        }
        
        // # Add meeting notifications
        if (notificationState.meetings && Object.keys(notificationState.meetings).length > 0) {
          Object.values(notificationState.meetings).forEach(notif => {
            // # Add meeting action to metadata
            const meetingNotif = {
              ...notif,
              metadata: {
                ...notif.metadata || {},
                action: 'meeting',
                createdAt: notif.created_at || new Date().toISOString()
              }
            };
            allNotifications.push(meetingNotif);
          });
        }
        
        // # Sort by creation date
        const sortedNotifications = allNotifications
          .sort((a, b) => new Date(b.metadata.createdAt) - new Date(a.metadata.createdAt));
        
        setNotifications(sortedNotifications);
      });
    }
  }, []);

  const handleNotificationClick = async (notificationId) => {
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      // # Mark notification as seen
      await notificationManager.markNotificationAsSeen(notificationId, session.uid);
      
      // # Update local state
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId ? { ...notif, seen: true } : notif
      ));
    } catch (error) {
      console.error('Error marking notification as seen:', error);
    }
  };

  const handleMarkAllAsSeen = async () => {
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      await notificationManager.markAllNotificationsAsSeen(session.uid);
      // # Update local state
      setNotifications(prev => prev.map(notif => ({ ...notif, seen: true })));
    } catch (error) {
      console.error('Error marking all notifications as seen:', error);
    }
  };

  const handleDeleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      // # Delete notification from session
      await notificationManager.deleteNotification(notificationId, session.uid);
      // # Update local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      // # Clear all notifications from session
      await notificationManager.clearAllNotifications(session.uid);
      // # Update local state
      setNotifications([]);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  const toggleExpand = (notificationId, event) => {
    event.stopPropagation();
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // # Helper function to format subscription type
  const getFormattedSubscriptionType = (type) => {
    if (!type) return 'Basic Plan';
    
    switch(type.toLowerCase()) {
      case 'step1_library':
        return 'USMLE Step 1 Library';
      case 'step2_library':
        return 'USMLE Step 2 Library';
      case 'step1_mentorship':
        return 'USMLE Step 1 Mentorship';
      case 'step2_mentorship':
        return 'USMLE Step 2 Mentorship';
      case 'oet':
        return 'OET Preparation';
      case 'us_visa_counselling':
        return 'US VISA Counselling';
      case 'gen_meetings':
        return 'General Meeting';
      case 'step1_meetings':
        return 'Step 1 Meeting';
      case 'step2_meetings':
        return 'Step 2 Meeting';
      case 'oet_meetings':
        return 'OET Meeting';
      case 'visa_meetings':
        return 'Visa Meeting';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  };

  // # Helper function to get notification text
  const getNotificationText = (notification) => {
    const action = notification.metadata.action;
    
    if (action === 'meeting') {
      return `New meeting: ${notification.title}`;
    }
    
    if (action === 'ending_soon') {
      const daysRemaining = notification.metadata.daysRemaining;
      return `Subscription ending in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
    }
    
    return action === 'activate' ? 'Subscription activated' :
           action === 'extend' ? 'Subscription extended' :
           action === 'reduce' ? 'Subscription duration reduced' :
           action === 'deactivate' ? 'Subscription deactivated' :
           action === 'ended' ? 'Subscription ended' :
           action === 'assign_mentor' ? 'Mentor assigned' :
           action === 'edit' ? 'Subscription updated' :
           'Subscription status changed';
  };

  return (
    <>
      <DashboardHeader />
      <div className="notif_page_wrapper">
        <div className="notif_page_container">
          <div className="notif_page_header">
            <div className="notif_page_title_container">
              <h1 className="notif_page_title">Your Notifications</h1>
              <div className="notif_page_title_decoration"></div>
            </div>
            <p className="notif_page_subtitle">Stay updated with your latest notifications</p>
            {notifications.length > 0 && (
              <div className="notif_page_actions">
                <button 
                  className="notif_page_mark_all" 
                  onClick={handleMarkAllAsSeen}
                >
                  <BsCheckCircle /> Mark all as read
                </button>
                <button 
                  className="notif_page_clear_all" 
                  onClick={handleClearAll}
                >
                  <BsTrash /> Clear all notifications
                </button>
              </div>
            )}
          </div>
          
          <div className="notif_page_list">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`notif_page_item ${notification.seen ? 'seen' : ''} ${expandedIds.has(notification.id) ? 'expanded' : ''}`}
                  onClick={(e) => toggleExpand(notification.id, e)}
                  data-action={notification.metadata.action}
                >
                  {getNotificationIcon(notification.metadata.action)}
                  <div className="notif_page_item_content">
                    <div className="notif_page_item_header">
                      <div>
                        <span className="notif_page_item_title">
                          {getNotificationText(notification)}
                        </span>
                        <span className="notif_page_item_time">
                          {new Date(notification.metadata.createdAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="notif_page_item_actions">
                        <BsCheckCircle 
                          className={`notif_page_item_check ${notification.seen ? 'seen' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notification.id);
                          }}
                          title="Mark as read"
                        />
                        <BsTrash 
                          className="notif_page_item_delete"
                          onClick={(e) => handleDeleteNotification(notification.id, e)}
                          title="Delete notification"
                        />
                      </div>
                    </div>
                    {notification.metadata.details && (
                      <p className="notif_page_item_details">
                        {notification.metadata.details}
                      </p>
                    )}
                    {expandedIds.has(notification.id) && (
                      <div className="notif_page_item_expanded">
                        {notification.metadata.action === 'meeting' ? (
                          <>
                            <p>Meeting Type: {getFormattedSubscriptionType(notification.type)}</p>
                            <p>Date & Time: {notification.meet_time ? 
                              (typeof notification.meet_time === 'string' ? 
                                new Date(notification.meet_time).toLocaleString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) 
                                : 
                                // # Handle Firebase Timestamp object
                                typeof notification.meet_time.toDate === 'function' ?
                                  notification.meet_time.toDate().toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                  :
                                  // # Handle Date object or timestamp stored differently
                                  new Date(notification.meet_time).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                              ) : 'Not specified'}
                            </p>
                            <Link 
                              to="/community/events/meetings" 
                              className="notif_page_view_meetings"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View in My Meetings
                            </Link>
                          </>
                        ) : (
                          <>
                            <p>Your Plan: {getFormattedSubscriptionType(notification.type)}</p>
                            <p>Active Until: {notification.endDate ? 
                              new Date(notification.endDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : 
                              'Ongoing subscription'}
                            </p>
                            <Link 
                              to="/settings/subscriptions" 
                              className="notif_page_view_subscriptions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Manage My Subscriptions
                            </Link>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="notif_page_empty">
                No notifications to display
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardFooter />
    </>
  );
};

export default UserNotificationsPage; 